import {CopyButton, Popover, Transition} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import classNames from 'classnames';
import {type FC, useEffect, useState} from 'react';
import {MdAutorenew, MdFileCopy, MdOpenInNew} from 'react-icons/md';
import {
  ENGINEER_COMPANY,
  ENGINEER_RECRUITING_INFO,
  PROFILE_IT_SKILL,
} from '../../../../../config/constants/routes';
import {ReferencesForTheme} from '../../../../../core/entities/entrysheetBuilder/constants';
import {DevelopmentExperienceFormLength} from '../../../../../core/entities/mypage/constants';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {RegistBox} from '../../../../../shared/components/RegistBox';
import {useHandleDevelopmentExperiences} from '../../../usecase/useHandleDevelopmentExperiences';
import {AddDevelopExperienceModal} from './AddDevelopExperienceModal';
import {EditButton} from './EditButton';
import {InputFreeTextarea} from './InputFreeTextarea';

const Typewriter: FC<{message: string; delay: number; isBlur?: boolean}> = ({
  message,
  delay,
  isBlur,
}) => {
  const [currentText, setCurrentText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < message.length) {
      const timeout = setTimeout(() => {
        setCurrentText((prevText) => prevText + message[currentIndex]);
        setCurrentIndex((prevIndex) => prevIndex + 1);
      }, delay);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [currentIndex, delay, message]);

  return (
    <p
      className={classNames('break-words whitespace-pre-wrap', {
        'blur-sm select-none': isBlur,
      })}
    >
      {currentText}
    </p>
  );
};

export const SystemMessage: FC<{
  message: string;
  isBlur?: boolean;
  typewriter?: boolean;
}> = ({message, isBlur, typewriter}) => {
  const [registMounted, setRegistMounted] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setRegistMounted(true);
    }, message.length * 40);
    return () => {
      clearTimeout(timer);
    };
  }, [message.length]);
  return (
    <div className="relative bg-background text-black-1 p-16 rounded-16 shadow-card w-fit">
      <div className="w-fit">
        {typewriter ? (
          <Typewriter message={message} delay={40} isBlur={isBlur} />
        ) : (
          <p
            className={classNames('break-words whitespace-pre-wrap', {
              'blur-sm select-none': isBlur,
            })}
          >
            {message}
          </p>
        )}

        {isBlur && (
          <Transition mounted={registMounted} transition="fade" duration={1000}>
            {(styles) => (
              <div className="w-fit" style={styles}>
                <RegistBox text={'生成結果を確認するには会員登録が必要です'} />
              </div>
            )}
          </Transition>
        )}
        <div className="absolute top-1/2 left-[-8px] transform -translate-y-1/2 w-0 h-0 border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent border-r-[10px] border-r-background" />
      </div>
    </div>
  );
};

export const GeneratedSystemMessage: FC<{
  isValid?: boolean;
  themeId: number;
  message: string;
  isModeration: boolean;
  rerunClick?: () => void;
  isShowAddToProfileButton?: boolean;
  isOpenModal?: boolean;
  selectedHashtagIds?: number[];
}> = ({
  isValid,
  themeId,
  message,
  isModeration,
  rerunClick,
  isShowAddToProfileButton = false,
  isOpenModal = false,
  selectedHashtagIds = [],
}) => {
  const [displayMessage, setDisplayMessage] = useState(message);
  const [editedMessage, setEditedMessage] = useState(message);
  const [editing, setEditing] = useState(false);

  const [devExperienceBody, setDevExperienceBody] = useState('');
  const [devExperienceHashtagIds, setDevExperienceHashtagIds] = useState<
    number[]
  >([]);
  const [addToProfileModal, {open, close}] = useDisclosure();
  const openAddToProfileModal = () => {
    setDevExperienceBody(displayMessage);
    setDevExperienceHashtagIds(selectedHashtagIds);
    open();
  };

  // IsModerationがtrueの場合、不適切な表現が含まれている旨をユーザーに表示する
  const isBlur = !isValid && isModeration;

  return (
    <>
      <div className="flex flex-col mt-16 gap-16">
        <div className="flex flex-col pc:flex-row gap-8">
          <SystemMessage
            message={displayMessage}
            isBlur={isBlur}
            typewriter={displayMessage === message}
          />
          <div className="flex flex-col items-end pc:justify-end pc:items-center p-8 gap-4">
            <div className="flex gap-8">
              <CopyButtonWithPopover
                copyValue={displayMessage}
                isClickable={isValid ?? false}
              />
              <RerunButton onClick={rerunClick} />
              <EditButton
                editing={editing}
                editClick={() => {
                  setEditing(!editing);
                }}
                isBgCircle={true}
                disabled={typeof isValid === 'undefined' || !isValid}
              />
            </div>
            <div className="">
              <p className="p-4 text-black-1">{`${displayMessage.length}文字`}</p>
            </div>
            {/* {resetClick && <ResetButton onClick={resetClick} />} */}
          </div>
        </div>
        {isValid && isShowAddToProfileButton && (
          <OpenAddToProfileModalButton
            openAddToProfileModal={openAddToProfileModal}
            isOpenModal={isOpenModal}
          />
        )}
        <References themeId={themeId} />
        <LinkList />
        {editing && (
          <div className="py-16">
            <InputFreeTextarea
              textInput={editedMessage}
              editing={editing}
              setTextInput={setEditedMessage}
              sendClick={() => {
                setEditing(false);
                setDisplayMessage(editedMessage);
              }}
            />
          </div>
        )}
      </div>

      {isValid && (
        <AddDevelopExperienceModal
          opened={addToProfileModal}
          close={close}
          devExperienceBody={devExperienceBody}
          setDevExperienceBody={setDevExperienceBody}
          devExperienceHashtagIds={devExperienceHashtagIds}
          setDevExperienceHashtagIds={setDevExperienceHashtagIds}
        />
      )}
    </>
  );
};

const CopyButtonWithPopover: FC<{
  copyValue: string;
  isClickable: boolean;
}> = ({copyValue, isClickable}) => {
  return (
    <div
      className={`p-8 rounded-16 bg-background w-[40px] h-[40px] ${
        isClickable
          ? 'hover:opacity-60 cursor-pointer'
          : 'opacity-50 cursor-not-allowed'
      }`}
    >
      <CopyButton value={copyValue}>
        {({copied, copy}) => (
          <Popover opened={copied}>
            <Popover.Target>
              <button
                type="button"
                className={classNames('text-black-2', {
                  'opacity-50 cursor-not-allowed': !isClickable,
                })}
                onClick={isClickable ? copy : undefined}
                disabled={!isClickable}
              >
                <MdFileCopy size={24} />
              </button>
            </Popover.Target>
            <Popover.Dropdown>コピーしました</Popover.Dropdown>
          </Popover>
        )}
      </CopyButton>
    </div>
  );
};

const RerunButton: FC<{
  onClick?: () => void;
}> = ({onClick}) => {
  const isClickable = onClick !== undefined;
  return (
    <div
      className={`p-8 rounded-16 bg-background w-[40px] h-[40px] ${
        isClickable
          ? 'hover:opacity-60 cursor-pointer'
          : 'opacity-50 cursor-not-allowed'
      }`}
    >
      <button
        type="button"
        className={classNames('text-black-2', {
          'opacity-50 cursor-not-allowed': !isClickable,
        })}
        onClick={onClick}
      >
        <MdAutorenew size={24} />
      </button>
    </div>
  );
};

const OpenAddToProfileModalButton: FC<{
  openAddToProfileModal?: () => void;
  isOpenModal?: boolean;
}> = ({openAddToProfileModal, isOpenModal = false}) => {
  const {experiencesLength} = useHandleDevelopmentExperiences({});
  if (experiencesLength === DevelopmentExperienceFormLength) return;

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        className="block w-[300px] p-[2px] m-auto bg-gradient-to-r from-gradient-ai-l to-gradient-ai-r rounded-full cursor-pointer hover:opacity-60"
        onClick={() => {
          if (isOpenModal && openAddToProfileModal) {
            return openAddToProfileModal();
          }
          return window.open(PROFILE_IT_SKILL, '_blank');
        }}
      >
        <div className="rounded-full px-16 py-8 bg-white">
          <div className="text-base bg-gradient-to-r from-gradient-ai-l to-gradient-ai-r text-transparent bg-clip-text">
            生成した内容をプロフィールに登録する
          </div>
        </div>
      </button>
      <div className="text-black-1 text-center text-sm">
        ※プロフィールに登録することで、エンジニア向けスカウトが5倍届きやすくなります
      </div>
    </div>
  );
};

const References: FC<{themeId: number}> = ({themeId}) => {
  const references = ReferencesForTheme.find(
    (item) => item.themeId === themeId,
  );
  return (
    <>
      {references && (
        <div className="px-8 text-black-1">
          <p className="font-bold">本選考を突破したエントリーシート</p>
          <ul className="">
            {references?.references.map((reference) => (
              <li key={reference.url} className="">
                <CommonLink
                  className="text-black-1 w-fit underline line-clamp-1 mb-4 hover:opacity-60"
                  href={reference.url}
                  target="_blank"
                >
                  {reference.title}
                  <MdOpenInNew size={16} className="inline-block ml-4" />
                </CommonLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

const LinkList: FC = () => (
  <div className="px-8 mt-8 flex flex-col gap-4">
    <p className="font-bold">エンジニア採用のおすすめ情報</p>
    <CommonLink
      className="text-black-1 w-fit underline line-clamp-1 hover:opacity-60"
      href={ENGINEER_RECRUITING_INFO}
      target="_blank"
    >
      募集情報はこちら
      <MdOpenInNew size={16} className="inline-block ml-4" />
    </CommonLink>
    <CommonLink
      className="text-black-1 w-fit underline line-clamp-1 hover:opacity-60"
      href={ENGINEER_COMPANY}
      target="_blank"
    >
      企業情報はこちら
      <MdOpenInNew size={16} className="inline-block ml-4" />
    </CommonLink>
  </div>
);
